import type { AiCapabilityPage } from "../schema.js";
import { MANAGER_ROLES, SLOT_FUND, WRITE_FLAGS } from "./_shared.js";

/**
 * `EXP-F-ADVANCE` (@UC0358 · US-AIV2-0113 ADVANCE-SHORTFALL · QĐ PO 30/09 phương án B) — ghi khoản chi QUỸ TRẢ khi quỹ KHÔNG ĐỦ:
 * **quỹ trả phần đang chi được, một người ứng phần còn thiếu, quỹ ghi nợ lại người đó**. T2.
 *
 * Executor = `expenseAdvance.create` ⇒ **CÙNG hàm với `EXP-F-NOALLOC`** (`ExpenseService.create`, `POST /v1/funds/:id/expenses`),
 * chỉ thêm đúng một field của hợp đồng GUI: `advancedByMemberId` (@UC9523). Cơ chế ghi là của lõi, không của trang:
 * `ExpenseFanOutPoster._coverShortfall` ghi `ADVANCE_CREDIT` (DEBIT CASH / CREDIT MEMBER_PAYABLE) cho ĐÚNG phần thiếu, CÙNG tx với
 * `EXPENSE`, rồi `EarmarkGuardService.assertSpendAllowed` vẫn là cổng duy nhất quyết (§3.8). Trang này KHÔNG có cơ chế ghi tiền thứ hai.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
 * 🔴🔴 KHÁC `EXP-F-EQUAL` (chia đều, người ứng là chủ nợ PEER) — đọc trước khi "gộp cho gọn":
 *  · `EXP-F-EQUAL`: người ứng trả CẢ khoản, cả nhóm nợ người đó từng suất (peer-debt, số dư quỹ bất biến).
 *  · trang NÀY: quỹ trả phần có, người ứng bù phần thiếu ⇒ QUỸ nợ người đó (fund-flow, `MEMBER_PAYABLE`). **Không chia ai, không
 *    peer.** QĐ PO 30/09 (M06 #1094 M2) chọn đúng nghĩa này cho câu "có người ứng tiền trước" trên phiếu quỹ trả đang thiếu.
 *
 * 🔴 Ô `nguoiUng` BẮT BUỘC và KHAI TƯỜNG MINH (§3.6) — không suy từ người bấm Đồng ý (thủ quỹ bấm, người ứng thường là người khác).
 *   0 hoặc ≥2 người khớp ⇒ hỏi (resolver). Phạm vi `payer` = CÙNG luật "ai được ứng tiền" của `EXP-F-EQUAL` (tập con của luật lõi
 *   `_coverShortfall`: ghế `active` của nhóm sở hữu quỹ) ⇒ nút chọn không mời vào cửa khoá.
 *
 * 🔴 SỐ trên phiếu KHÔNG từ chữ, KHÔNG từ LLM: "quỹ trả" và "người ứng bù" do binding tính bằng `EarmarkGuardService.summarize` +
 *   `checkGeneralSpend` (CÙNG công thức chốt trong tx dùng); ô tiền duy nhất là CẢ khoản. Quỹ đủ ⇒ binding TỪ CHỐI (ứng khống = nợ ma;
 *   lõi cũng lặng lẽ bỏ qua người ứng khi quỹ đủ ⇒ phiếu sẽ nói dối). Lúc Đồng ý: số thiếu tính lại trong tx phải KHỚP phiếu, và bút
 *   toán `ADVANCE_CREDIT` lõi vừa ghi phải đúng số đó — lệch ⇒ rollback, 0 bút toán (AI-INV "phiếu == cái được ghi").
 *
 * Lối vào chính: nút "Có người ứng tiền trước" trên phiếu `EXP-F-NOALLOC` khi dòng ⚠️ (M06) nói quỹ không đủ — Người làm chuyển sang
 * trang này mang các ô CHUNG (quỹ · số tiền · nội dung · ngày) đã resolve phía server của phiếu cũ; ô người ứng luôn phải hỏi.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * 🔴🔴 CHỈ MỞ BẰNG NÚT (`entry: "chip-only"`, QĐ điều phối 01/10 sau NEED lượt 3): bản đầu cho Người phiên dịch chọn trang này bằng chữ (40
 * câu ví dụ, cụm hiếm "hụt"/"ứng bù") ⇒ đo n=5 trang này HÚT câu chi thường của `EXP-F-NOALLOC` ("ghi khoan chi 2tr thue xe quy tra het":
 * develop 5/5 · nhánh 0/5) và `EXP-F-EQUAL` ("An ứng 1tr5 …, chia đều": 5/5 · 1/5). Nay: KHÔNG câu ví dụ, KHÔNG vào chỉ mục tìm trang /
 * prompt / menu / bộ câu chuẩn; người nói gì về chi tiền vẫn vào NOALLOC/EQUAL y như develop. Cửa DUY NHẤT: nút "Có người ứng tiền
 * trước" trên phiếu NOALLOC (cờ `__advanceInvite`, chỉ nhóm GHI SỔ NGAY — review #1101 H1). `llm.description` còn vì schema chung, không
 * tới model. Số đo + bằng chứng: `_runs/US-AIV2-0113/ADVANCE-SHORTFALL/README.md`.
 *
 * Status `bound`, KHÔNG `live`: luật `@UC0159` (live đòi eval xanh, G14k) không nới — trang không có ca eval nào. ⚠️ Hệ quả: `isRunnable`
 * ở production chỉ nhận `live` ⇒ nút ứng bù chưa hiện ở môi trường production cho tới khi có QĐ PO về eval của trang chỉ-mở-bằng-nút.
 */
export const EXP_F_ADVANCE = {
  id: "EXP-F-ADVANCE",
  uc: "@UC0358",
  tier: "T2",
  status: "bound",
  /** QĐ điều phối 01/10 — xem docblock: chỉ mở bằng nút của phiếu `EXP-F-NOALLOC`. */
  entry: "chip-only",
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    { ...SLOT_FUND, clarify: "Chi từ quỹ nào?" },
    { id: "soTien", type: "money", required: true, ask: "Cả khoản chi bao nhiêu?" },
    { id: "noiDung", type: "text", required: true, ask: "Chi cho việc gì?" },
    { id: "nguoiUng", type: "member", required: true, ask: "Ai đã ứng phần quỹ còn thiếu?", memberScope: "payer" },
    { id: "ngay", type: "date", required: false, ask: "Chi ngày nào? (bỏ trống = hôm nay)" },
  ],
  llm: {
    description: "Khoản chi lúc quỹ hụt tiền: một người ứng bù phần quỹ hụt, quỹ ghi nợ lại người ứng.",
    examples: [],
  },
  executor: "expenseAdvance.create",
  /** `@UC0216` — cùng cửa `POST /funds/{fundId}/expenses` với `EXP-F-NOALLOC` (trang biến thể) ⇒ khai rỗng. */
  endpoints: [],
  facts: ["tenQuy", "ngayGhi", "noiDungGhi", "quyTra", "soThieu", "tenNguoiUng", "ketQua"],
  templates: {
    confirm:
      "Ghi chi {soTien} “{noiDungGhi}” từ quỹ {tenQuy}, ngày {ngayGhi}: Quỹ trả {quyTra} · {tenNguoiUng} ứng {soThieu} · Quỹ nợ lại {tenNguoiUng} {soThieu}?",
    /** Kết cục theo `status` THẬT mà `ExpenseService.create` trả — hệ quả ở GIÁ TRỊ `{ketQua}` (khuôn `ketQuaGhiChi` của 0041). */
    done: "Đã ghi chi {soTien} “{noiDungGhi}”. {ketQua}",
  },
  labels: {
    soTien: "Cả khoản chi",
    noiDung: "Nội dung",
    tenQuy: "Quỹ",
    ngayGhi: "Ngày chi",
    noiDungGhi: "Nội dung",
    quyTra: "Quỹ trả",
    soThieu: "Người ứng bù",
    tenNguoiUng: "Người ứng",
    ketQua: "Kết quả",
  },
  /**
   * 🔴 QĐ PO 30/09 (review #1101 H1): trang này CHỈ ghi ở quỹ GHI SỔ NGAY (số trên thẻ = số được ghi). Nhóm có duyệt chi / thời gian chờ ⇒
   * binding từ chối `AI_EXP_F_ADVANCE_NOT_IMMEDIATE` và nút "đi tiếp" mở THẲNG màn nhóm (nơi có nút ghi chi — mobile không có route
   * form chi riêng). Không vào prompt/chỉ mục tìm trang ⇒ 0 ca eval đổi.
   */
  guiScreen: "/groups/:groupId",
  // 🔴 US-AIV2-tourguide-0008 (@UC0362): màn nhóm có "Chi quỹ" (số tiền · ngày · tên — KHÔNG ô người ứng bù, khảo sát app 05/10) ⇒ điều nút
  // khẳng định là "ghi khoản chi", không phải "ghi chi có người ứng bù". Nhãn nút lỗi theo đó (`FAILURE_GUI_SCREEN`, ai-doer.service.ts).
  screenShows: "expense_record",
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY"] } } },
  // chip-only: không vào menu/trợ giúp (`isAiChatRunnablePage`); `tryNow` nói CỬA THẬT — nút trên phiếu chi quỹ trả, không phải một câu gõ.
  help: { title: "Ghi chi khi quỹ hụt (có người ứng bù)", tryNow: "ghi khoản chi quỹ trả, rồi bấm “Có người ứng tiền trước” khi quỹ không đủ" },
} satisfies AiCapabilityPage;
